import { useCallback, useMemo, useState } from "react";
import { Clipboard, Heart, Search, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { usePromptLibrary } from "@/hooks/usePromptLibrary";
import { copyTextSafely } from "@/lib/clipboard";

const categoryOptions = [
  "Kaikki",
  "Lyhytvideot",
  "Kuvatekstit ja hashtagit",
  "Sisältöideat",
  "AI-kuvat",
  "AI-videot",
  "UGC",
  "Mainostekstit",
  "Sähköpostit",
  "Myyntisivut",
  "Suno",
  "ElevenLabs",
  "Someanalytiikka",
];

const categoryForItem = (item) => {
  const type = item.type || "";
  const aiTool = item.aiTool || "";
  if (type.includes("Lyhytvideo")) return "Lyhytvideot";
  if (type.includes("Kuvatekstit")) return "Kuvatekstit ja hashtagit";
  if (type.includes("Sisältöideat")) return "Sisältöideat";
  if (type.includes("AI-kuvat")) return "AI-kuvat";
  if (type.includes("AI-videot")) return "AI-videot";
  if (type.includes("UGC")) return "UGC";
  if (type.includes("Mainostekstit")) return "Mainostekstit";
  if (type.includes("Sähköposti")) return "Sähköpostit";
  if (type.includes("Myyntisivut")) return "Myyntisivut";
  if (type.includes("Suno") || aiTool.includes("Suno")) return "Suno";
  if (type.includes("Äänitehosteet") || aiTool.includes("ElevenLabs")) return "ElevenLabs";
  if (type.includes("Someanalytiikka")) return "Someanalytiikka";
  return type || "Muut";
};

export default function LibraryPage() {
  const { items, removePrompt, toggleFavorite } = usePromptLibrary();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("Kaikki");
  const [aiTool, setAiTool] = useState("Kaikki työkalut");
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  const aiToolOptions = useMemo(() => ["Kaikki työkalut", ...Array.from(new Set(items.map((item) => item.aiTool).filter(Boolean))).sort((a, b) => a.localeCompare(b, "fi"))], [items]);

  const filteredItems = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return [...items]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .filter((item) => (favoritesOnly ? item.favorite : true))
      .filter((item) => (category === "Kaikki" ? true : categoryForItem(item) === category))
      .filter((item) => (aiTool === "Kaikki työkalut" ? true : item.aiTool === aiTool))
      .filter((item) => {
        if (!normalizedQuery) return true;
        return [item.title, item.type, item.aiTool, item.prompt].filter(Boolean).join(" ").toLowerCase().includes(normalizedQuery);
      });
  }, [aiTool, category, favoritesOnly, items, query]);

  const copy = useCallback(async (prompt) => {
    const copied = await copyTextSafely(prompt);
    if (copied) {
      toast.success("Prompti kopioitu!");
    } else {
      toast.error("Kopiointi epäonnistui. Kopioi prompti käsin tekstikentästä.");
    }
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="library-page">
      <div className="mb-8 max-w-3xl" data-testid="library-heading-block">
        <p className="text-sm font-extrabold uppercase text-[#D97757]" data-testid="library-kicker">Oma promptikirjasto</p>
        <h1 className="mt-2 font-heading text-4xl font-bold text-[#2C2F2E] sm:text-5xl" data-testid="library-title">Tallennetut promptit</h1>
        <p className="mt-4 text-base leading-7 text-[#5C6360]" data-testid="library-description">Promptit tallentuvat tähän selaimeen paikallisesti. Voit kopioida tai poistaa niitä milloin tahansa.</p>
        <p className="mt-3 rounded-2xl bg-[#F5F3EC] px-4 py-3 text-sm leading-6 text-[#5C6360]" data-testid="library-local-storage-notice">Huom! Promptikirjasto tallentuu tässä versiossa selaimesi muistiin. Jos tyhjennät selaimen tiedot tai vaihdat laitetta, tallennetut promptit eivät välttämättä säily.</p>
      </div>
      {items.length === 0 ? (
        <section className="rounded-[1.75rem] border border-dashed border-[#D8D2C1] bg-white p-10 text-center" data-testid="library-empty-state">
          <p className="font-heading text-2xl font-bold text-[#2C2F2E]" data-testid="library-empty-title">Kirjasto on vielä tyhjä</p>
          <p className="mt-3 text-sm leading-7 text-[#5C6360]" data-testid="library-empty-description">Luo prompti tai tallenna valmis pohja, niin se ilmestyy tänne.</p>
        </section>
      ) : (
        <>
          <section className="mb-6 rounded-[1.75rem] border border-[#E8E5D9] bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="library-filters-section">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(220px,0.6fr)_minmax(220px,0.6fr)_auto] lg:items-end" data-testid="library-filters-grid">
              <div data-testid="library-search-wrapper">
                <label htmlFor="library-search-input" className="mb-2 block text-sm font-bold text-[#2C2F2E]" data-testid="library-search-label">Haku</label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5C6360]" aria-hidden="true" />
                  <input id="library-search-input" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Hae promptin nimellä, kategorialla tai sisällöllä…" className="w-full rounded-2xl border border-transparent bg-[#F5F3EC] py-3 pl-11 pr-4 text-sm text-[#2C2F2E] outline-none transition-colors duration-200 placeholder:text-[#5C6360]/55 focus:border-[#4A6753]/40 focus:bg-white focus:ring-4 focus:ring-[#4A6753]/10" data-testid="library-search-input" />
                </div>
              </div>
              <div data-testid="library-category-wrapper">
                <label htmlFor="library-category-filter" className="mb-2 block text-sm font-bold text-[#2C2F2E]" data-testid="library-category-label">Kategoria</label>
                <select id="library-category-filter" value={category} onChange={(event) => setCategory(event.target.value)} className="w-full rounded-2xl border border-transparent bg-[#F5F3EC] px-4 py-3 text-sm text-[#2C2F2E] outline-none transition-colors duration-200 focus:border-[#4A6753]/40 focus:bg-white focus:ring-4 focus:ring-[#4A6753]/10" data-testid="library-category-filter">
                  {categoryOptions.map((option) => <option key={option} value={option} data-testid={`library-category-option-${option.toLowerCase().replaceAll(" ", "-")}`}>{option}</option>)}
                </select>
              </div>
              <div data-testid="library-ai-tool-wrapper">
                <label htmlFor="library-ai-tool-filter" className="mb-2 block text-sm font-bold text-[#2C2F2E]" data-testid="library-ai-tool-label">AI-työkalu</label>
                <select id="library-ai-tool-filter" value={aiTool} onChange={(event) => setAiTool(event.target.value)} className="w-full rounded-2xl border border-transparent bg-[#F5F3EC] px-4 py-3 text-sm text-[#2C2F2E] outline-none transition-colors duration-200 focus:border-[#4A6753]/40 focus:bg-white focus:ring-4 focus:ring-[#4A6753]/10" data-testid="library-ai-tool-filter">
                  {aiToolOptions.map((option) => <option key={option} value={option} data-testid={`library-ai-tool-option-${option.toLowerCase().replaceAll(" ", "-")}`}>{option}</option>)}
                </select>
              </div>
              <label className={`inline-flex min-h-[46px] items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-bold transition-colors ${favoritesOnly ? "bg-[#4A6753] text-white" : "bg-[#F5F3EC] text-[#2C2F2E] hover:bg-[#E8E5D9]"}`} data-testid="library-favorites-filter-label">
                <input type="checkbox" checked={favoritesOnly} onChange={(event) => setFavoritesOnly(event.target.checked)} className="sr-only" data-testid="library-favorites-filter-checkbox" />
                <Heart className={`h-4 w-4 ${favoritesOnly ? "fill-white" : ""}`} aria-hidden="true" />
                Näytä suosikit
              </label>
            </div>
            <p className="mt-4 text-sm text-[#5C6360]" data-testid="library-result-count">Näytetään {filteredItems.length} / {items.length} promptia, uusimmat ensin.</p>
          </section>

          {filteredItems.length === 0 ? (
            <section className="rounded-[1.75rem] border border-dashed border-[#D8D2C1] bg-white p-10 text-center" data-testid="library-no-results-state">
              <p className="font-heading text-2xl font-bold text-[#2C2F2E]" data-testid="library-no-results-title">Ei osumia</p>
              <p className="mt-3 text-sm leading-7 text-[#5C6360]" data-testid="library-no-results-description">Kokeile toista hakusanaa tai poista suodatin.</p>
            </section>
          ) : (
            <div className="grid grid-cols-1 gap-5" data-testid="library-items-list">
              {filteredItems.map((item) => (
            <article key={item.id} className="rounded-[1.75rem] border border-[#E8E5D9] bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`library-item-${item.id}`}>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-testid={`library-item-heading-${item.id}`}>
                <div>
                  <p className="text-xs font-extrabold uppercase text-[#D97757]" data-testid={`library-item-type-${item.id}`}>{item.type}</p>
                  <h2 className="mt-1 font-heading text-2xl font-bold text-[#2C2F2E]" data-testid={`library-item-title-${item.id}`}>{item.title}</h2>
                  {item.aiTool && <p className="mt-1 text-xs font-bold text-[#4A6753]" data-testid={`library-item-ai-tool-${item.id}`}>AI-työkalu: {item.aiTool}</p>}
                  <p className="mt-1 text-xs text-[#5C6360]" data-testid={`library-item-date-${item.id}`}>{new Date(item.createdAt).toLocaleString("fi-FI")}</p>
                </div>
                <div className="flex flex-wrap gap-2" data-testid={`library-item-actions-${item.id}`}>
                  <button type="button" onClick={() => toggleFavorite(item.id)} className={`inline-flex items-center gap-2 rounded-full px-4 py-3 text-sm font-bold transition-transform duration-200 hover:-translate-y-0.5 ${item.favorite ? "bg-[#D97757] text-white" : "bg-[#F5F3EC] text-[#2C2F2E]"}`} data-testid={`library-favorite-button-${item.id}`} aria-pressed={item.favorite}>
                    <Heart className={`h-4 w-4 ${item.favorite ? "fill-white" : ""}`} aria-hidden="true" /> {item.favorite ? "Suosikeissa" : "Lisää suosikkeihin"}
                  </button>
                  <button type="button" onClick={() => copy(item.prompt)} className="inline-flex items-center gap-2 rounded-full bg-[#4A6753] px-4 py-3 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5" data-testid={`library-copy-button-${item.id}`}>
                    <Clipboard className="h-4 w-4" aria-hidden="true" /> Kopioi
                  </button>
                  <button type="button" onClick={() => removePrompt(item.id)} className="inline-flex items-center gap-2 rounded-full bg-[#F5F3EC] px-4 py-3 text-sm font-bold text-[#2C2F2E] transition-transform duration-200 hover:-translate-y-0.5" data-testid={`library-delete-button-${item.id}`}>
                    <Trash2 className="h-4 w-4" aria-hidden="true" /> Poista
                  </button>
                </div>
              </div>
              <p className="mt-5 whitespace-pre-wrap rounded-[1.25rem] bg-[#F5F3EC] p-4 text-sm leading-7 text-[#2C2F2E]" data-testid={`library-item-prompt-${item.id}`}>{item.prompt}</p>
            </article>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}